/**
 * ReceiveMoneyDialog — iter231
 *
 * The unified "Receive Money" wizard for the super admin. 3 steps:
 *   1. Who paid + how much + method + note
 *   2. Where does it go? (Wallet / Group contribution / Loan repayment / P2P settle)
 *   3. Destination-specific picker (group / loan / recipient)
 *
 * Fires POST /api/wallet/receive-money atomically. Shows a WhatsApp share
 * button on the success screen so admin can confirm the payer.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Banknote, Users, HandCoins, ArrowLeftRight, Wallet as WalletIcon,
  ChevronRight, ChevronLeft, Loader2, Check, Share2, X,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const METHODS = [
  { id: 'cash', label: 'Cash' },
  { id: 'gpay', label: 'GPay / UPI' },
  { id: 'imps', label: 'IMPS / NEFT' },
  { id: 'cheque', label: 'Cheque' },
  { id: 'other', label: 'Other' },
];

const DESTINATIONS = [
  { id: 'wallet', label: 'Trust Wallet', help: 'Cash sits in the payer\'s wallet, unassigned.', Icon: WalletIcon },
  { id: 'group_fund', label: 'Group Contribution', help: 'Cash → wallet → auto-bridged into a group fund.', Icon: Users },
  { id: 'loan_repayment', label: 'Loan Repayment', help: 'Reduce the payer\'s outstanding loan balance.', Icon: HandCoins },
  { id: 'p2p_settle', label: 'Pass to Member', help: 'You received cash on behalf of another member.', Icon: ArrowLeftRight },
];

export default function ReceiveMoneyDialog({ open, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loans, setLoans] = useState([]);

  const [payerId, setPayerId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [destination, setDestination] = useState('wallet');
  const [groupId, setGroupId] = useState('');
  const [loanId, setLoanId] = useState('');
  const [recipientId, setRecipientId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setPayerId(''); setAmount(''); setMethod('cash'); setNote('');
    setDestination('wallet'); setGroupId(''); setLoanId(''); setRecipientId('');
    setResult(null);
    // Load reference data
    (async () => {
      try {
        const [u, g, l] = await Promise.all([
          api.get('/admin/wallets'),
          api.get('/groups'),
          api.get('/admin/loans?status=active'),
        ]);
        setUsers(u.data?.rows || []);
        setGroups(g.data?.groups || g.data || []);
        setLoans(l.data?.rows || []);
      } catch (e) { /* silent */ }
    })();
  }, [open]);

  const payer = useMemo(() => users.find((u) => u.user_id === payerId), [users, payerId]);
  const payerGroups = useMemo(
    () => (groups || []).filter((g) => (g.member_ids || []).includes(payerId)),
    [groups, payerId],
  );
  const payerLoans = useMemo(
    () => (loans || []).filter((l) => l.user_id === payerId && l.status !== 'repaid'),
    [loans, payerId],
  );

  const canNextStep1 = payerId && Number(amount) > 0;
  const canNextStep2 = !!destination;
  const canSubmit = useMemo(() => {
    if (destination === 'wallet') return true;
    if (destination === 'group_fund') return !!groupId;
    if (destination === 'loan_repayment') return !!loanId;
    if (destination === 'p2p_settle') return !!recipientId && recipientId !== payerId;
    return false;
  }, [destination, groupId, loanId, recipientId, payerId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        payer_id: payerId,
        amount: Number(amount),
        method,
        note: note?.trim() || null,
        destination,
        group_id: destination === 'group_fund' ? groupId : null,
        loan_id: destination === 'loan_repayment' ? loanId : null,
        recipient_id: destination === 'p2p_settle' ? recipientId : null,
      };
      const r = await api.post('/wallet/receive-money', body);
      setResult({ ...r.data, payer_name: payer?.name, amount, destination });
      setStep(4);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record');
    } finally {
      setSubmitting(false);
    }
  };

  const buildShareText = () => {
    if (!result) return '';
    const nm = result.payer_name || 'the payer';
    const amt = `₹${Number(amount).toLocaleString('en-IN')}`;
    if (destination === 'wallet') return `Hi ${nm}, confirming I received ${amt} from you via ${method}. It's credited to your SplitEasy wallet. - Kalpesh`;
    if (destination === 'group_fund') {
      const g = groups.find((x) => x.id === groupId);
      return `Hi ${nm}, confirming I received ${amt} from you via ${method}. It's contributed to '${g?.name || 'the group'}'. - Kalpesh`;
    }
    if (destination === 'loan_repayment') return `Hi ${nm}, confirming I received ${amt} loan repayment via ${method}. Outstanding updated on SplitEasy. - Kalpesh`;
    if (destination === 'p2p_settle') {
      const rc = users.find((u) => u.user_id === recipientId);
      return `Hi ${nm}, confirming I received ${amt} from you via ${method}. It's been passed to ${rc?.name || 'the recipient'} on SplitEasy. - Kalpesh`;
    }
    return '';
  };

  const doShare = () => {
    const text = buildShareText();
    const phone = payer?.phone?.replace(/\D/g, '');
    if (phone && /^\d{10,15}$/.test(phone)) {
      window.open(`https://wa.me/${phone.startsWith('91') ? phone : `91${phone}`}?text=${encodeURIComponent(text)}`, '_blank');
    } else if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(text);
      toast.success('Message copied');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose?.()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="receive-money-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-600" /> Receive Money
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Step 1 of 3 · Who paid and how much?'}
            {step === 2 && 'Step 2 of 3 · Where does it go?'}
            {step === 3 && 'Step 3 of 3 · Confirm details'}
            {step === 4 && 'Recorded — audit trail saved'}
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        {step < 4 && (
          <div className="flex gap-1 my-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-primary' : 'bg-muted'}`} />
            ))}
          </div>
        )}

        {/* ---- Step 1: Payer + Amount + Method ---- */}
        {step === 1 && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Who paid</Label>
              <Select value={payerId} onValueChange={setPayerId}>
                <SelectTrigger data-testid="rm-payer-select"><SelectValue placeholder="Pick a person" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} <span className="text-muted-foreground text-xs ml-1">· {u.phone}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                type="number"
                min={1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                data-testid="rm-amount"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Payment method</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                {METHODS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMethod(m.id)}
                    className={`p-2 border-2 rounded-lg text-xs font-semibold transition-colors ${
                      method === m.id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                    }`}
                    data-testid={`rm-method-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Note (optional — GPay txn ID, cheque #, etc.)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="rm-note" />
            </div>
          </div>
        )}

        {/* ---- Step 2: Destination picker ---- */}
        {step === 2 && (
          <div className="space-y-2">
            {DESTINATIONS.map((d) => {
              const Icon = d.Icon;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDestination(d.id)}
                  className={`w-full text-left p-3 border-2 rounded-xl transition-colors flex items-start gap-3 ${
                    destination === d.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                  }`}
                  data-testid={`rm-dest-${d.id}`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${destination === d.id ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{d.label}</p>
                    <p className="text-xs text-muted-foreground">{d.help}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* ---- Step 3: Confirm ---- */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="bg-muted/40 border rounded-xl p-3 text-sm space-y-1">
              <p><span className="text-muted-foreground">Payer:</span> <strong>{payer?.name}</strong></p>
              <p><span className="text-muted-foreground">Amount:</span> <strong>₹{Number(amount).toLocaleString('en-IN')}</strong></p>
              <p><span className="text-muted-foreground">Method:</span> {METHODS.find((m) => m.id === method)?.label}</p>
              {note && <p><span className="text-muted-foreground">Note:</span> {note}</p>}
              <p><span className="text-muted-foreground">Destination:</span> <strong>{DESTINATIONS.find((d) => d.id === destination)?.label}</strong></p>
            </div>

            {destination === 'group_fund' && (
              <div>
                <Label className="text-xs">Which group?</Label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger data-testid="rm-group-select"><SelectValue placeholder="Pick a group" /></SelectTrigger>
                  <SelectContent>
                    {payerGroups.length === 0 && (
                      <SelectItem disabled value="_none">Payer is not in any group</SelectItem>
                    )}
                    {payerGroups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {destination === 'loan_repayment' && (
              <div>
                <Label className="text-xs">Which loan?</Label>
                <Select value={loanId} onValueChange={setLoanId}>
                  <SelectTrigger data-testid="rm-loan-select"><SelectValue placeholder="Pick an outstanding loan" /></SelectTrigger>
                  <SelectContent>
                    {payerLoans.length === 0 && <SelectItem disabled value="_none">No outstanding loans</SelectItem>}
                    {payerLoans.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        ₹{Number(l.outstanding).toLocaleString('en-IN')} outstanding · issued {new Date(l.created_at).toLocaleDateString('en-IN')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {destination === 'p2p_settle' && (
              <div>
                <Label className="text-xs">Pass to which member?</Label>
                <Select value={recipientId} onValueChange={setRecipientId}>
                  <SelectTrigger data-testid="rm-recipient-select"><SelectValue placeholder="Pick recipient" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {users.filter((u) => u.user_id !== payerId).map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {/* ---- Step 4: Success ---- */}
        {step === 4 && result && (
          <div className="space-y-3 text-center py-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg font-bold">Received ₹{Number(amount).toLocaleString('en-IN')}</p>
            <p className="text-sm text-muted-foreground">{result.message}</p>
            <div className="flex gap-2 justify-center pt-2">
              <Button variant="outline" onClick={doShare} data-testid="rm-share-btn">
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> WhatsApp Payer
              </Button>
              <Button onClick={() => onClose?.()} data-testid="rm-done-btn">Done</Button>
            </div>
          </div>
        )}

        {/* ---- Footer navigation ---- */}
        {step < 4 && (
          <div className="flex justify-between pt-3 border-t mt-2">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={submitting} data-testid="rm-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : (
              <Button variant="ghost" onClick={onClose} data-testid="rm-cancel-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            )}
            {step < 3 ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={(step === 1 && !canNextStep1) || (step === 2 && !canNextStep2)}
                data-testid="rm-next-btn"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="rm-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Record
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
